import {
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  type AgentLaunchSpec,
  type ModelSelection,
  type NativeApi,
  type ProjectId,
  type SetupHook,
} from "@taskin-code/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { useAppSettings } from "../appSettings";
import { useDefaultModel } from "../auth/authStore";
import { useModelSelectionPolicy } from "./useModelSelectionPolicy";
import {
  resolvePreferredComposerModelSelection,
  useComposerDraftStore,
} from "../composerDraftStore";
import { createFreshDraftThreadSeed } from "../lib/threadBootstrap";
import { newCommandId, newThreadId } from "../lib/utils";
import { readNativeApi } from "../nativeApi";
import { useStore } from "../store";
import { useTerminalStateStore } from "../terminalStateStore";
import { toastManager } from "../components/ui/toast";
import { describeWorktreeBaseFallback } from "../worktreeBase";

export interface NewWorktreeThreadError {
  readonly message: string;
  readonly detail?: string;
  readonly retry: () => Promise<void>;
}

export interface NewWorktreeThreadOptions {
  agentId?: string;
  baseBranch?: string;
}

const SETUP_OK_MARKER = "[taskin setup OK";
const SETUP_OUTPUT_TAIL_LIMIT = 32_768;

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, "'\\''")}'`;
}

function buildAgentLaunchScript(
  setupHook: SetupHook,
  agent: AgentLaunchSpec,
): { shell: string; shellArgs: string[] } {
  const agentCmdline = [agent.command, ...agent.args].map(shellQuote).join(" ");
  const total = setupHook.commands.length;
  const lines: string[] = ["set -e"];
  for (let i = 0; i < setupHook.commands.length; i += 1) {
    const cmd = setupHook.commands[i]!;
    lines.push(
      `printf '\\n\\x1b[1;36m[taskin setup %d/%d]\\x1b[0m %s\\n' ${i + 1} ${total} ${shellQuote(cmd)}`,
    );
    lines.push(cmd);
  }
  lines.push(
    `printf '\\n\\x1b[1;32m[taskin setup OK — launching agent]\\x1b[0m\\n'`,
    `exec ${agentCmdline}`,
  );
  return { shell: "/bin/bash", shellArgs: ["-lc", lines.join("\n")] };
}

type SetupOutcome = { ok: true } | { ok: false; exitCode: number | null; tail: string };

function subscribeSetupOutcome(
  api: NativeApi,
  threadId: string,
  terminalId: string,
): Promise<SetupOutcome> {
  return new Promise((resolve) => {
    let buffer = "";
    const append = (chunk: string) => {
      buffer = (buffer + chunk).slice(-SETUP_OUTPUT_TAIL_LIMIT);
    };
    const unsubscribe = api.terminal.onEvent((event) => {
      if (event.threadId !== threadId || event.terminalId !== terminalId) return;
      if (event.type === "output") {
        append(event.data);
        if (buffer.includes(SETUP_OK_MARKER)) {
          unsubscribe();
          resolve({ ok: true });
        }
        return;
      }
      if (event.type === "exited") {
        unsubscribe();
        if (buffer.includes(SETUP_OK_MARKER)) {
          resolve({ ok: true });
        } else {
          resolve({ ok: false, exitCode: event.exitCode, tail: buffer });
        }
        return;
      }
      if (event.type === "error") {
        append(`\n[error] ${event.message}`);
        unsubscribe();
        resolve({ ok: false, exitCode: null, tail: buffer });
      }
    });
  });
}

export function useHandleNewWorktreeThread() {
  const openTerminalThreadPage = useTerminalStateStore((store) => store.openTerminalThreadPage);
  const navigate = useNavigate();
  const { settings } = useAppSettings();
  const userDefaultModel = useDefaultModel();
  const modelSelectionPolicy = useModelSelectionPolicy();
  const [error, setError] = useState<NewWorktreeThreadError | null>(null);
  const [isPending, setIsPending] = useState(false);

  const dismissError = useCallback(() => setError(null), []);

  const handleNewWorktreeThread = useCallback(
    async (projectId: ProjectId, options?: NewWorktreeThreadOptions): Promise<void> => {
      setError(null);
      const api = readNativeApi();
      if (!api) return;

      const projectDefaultModelSelection =
        useStore.getState().projects.find((project) => project.id === projectId)
          ?.defaultModelSelection ?? null;

      const run = async () => {
        const threadId = newThreadId();
        const prep = await api.workspace.prepareAgentWorktree({
          projectId,
          sessionId: threadId,
          ...(options?.agentId !== undefined ? { agentId: options.agentId } : {}),
          ...(options?.baseBranch !== undefined ? { baseBranch: options.baseBranch } : {}),
        });

        // Same warning as the chat composer path: a thread that forked from a
        // stale base is indistinguishable from a fresh one until it conflicts.
        if (prep.base?.source === "local" && prep.base.reason !== "configured-local") {
          toastManager.add({
            type: "warning",
            title: `Se partió de la copia local de "${prep.baseBranch}"`,
            description: `${describeWorktreeBaseFallback(prep.base.reason)} La rama puede estar por detrás del remoto.`,
          });
        }

        const createdAt = new Date().toISOString();
        const modelSelection: ModelSelection = resolvePreferredComposerModelSelection({
          draft: null,
          threadModelSelection: null,
          projectModelSelection: projectDefaultModelSelection,
          userDefaultModelSelection: userDefaultModel,
          defaultProvider: settings.defaultProvider,
          policy: modelSelectionPolicy,
        });

        // Seed a draft thread before navigating so the route guard in
        // `_chat.$threadId.tsx` finds the thread (via draft fallback) and
        // doesn't redirect to `/`. Mirrors `useHandleNewThread`.
        useComposerDraftStore.getState().setProjectDraftThreadId(projectId, threadId, {
          ...createFreshDraftThreadSeed({
            createdAt,
            entryPoint: "terminal",
            options: {
              branch: prep.branch,
              worktreePath: prep.worktreePath,
              envMode: "local",
            },
          }),
        });

        const terminalId = "default";
        const { shell, shellArgs } = prep.setupHook
          ? buildAgentLaunchScript(prep.setupHook, prep.agent)
          : { shell: prep.agent.command, shellArgs: [...prep.agent.args] };

        const watcher = prep.setupHook ? subscribeSetupOutcome(api, threadId, terminalId) : null;

        // Spawn the agent PTY BEFORE the viewport mounts. The viewport's
        // auto-attach uses `attachOnly: true`, but the server only honors
        // that flag when a session already exists — otherwise it would
        // spawn a default zsh and our agent shell config would be ignored
        // (the existing session would just be reused).
        await api.terminal.open({
          threadId,
          terminalId,
          cwd: prep.worktreePath,
          shell,
          shellArgs,
          env: { ...prep.agent.env },
          exitOnShellExit: true,
        });

        // Navigate BEFORE dispatching `thread.create`. If we dispatched
        // first, the projection event would race with the route mount and
        // the route guard could see neither a real thread nor a draft,
        // redirecting to `/` and then to the first project's chat draft.
        openTerminalThreadPage(threadId, { terminalOnly: true });
        await navigate({ to: "/$threadId", params: { threadId } });

        await api.orchestration.dispatchCommand({
          type: "thread.create",
          commandId: newCommandId(),
          threadId,
          projectId,
          title: prep.agent.label,
          modelSelection,
          runtimeMode: DEFAULT_RUNTIME_MODE,
          interactionMode: DEFAULT_PROVIDER_INTERACTION_MODE,
          envMode: "local",
          branch: prep.branch,
          worktreePath: prep.worktreePath,
          associatedWorktreePath: prep.worktreePath,
          associatedWorktreeBranch: prep.branch,
          associatedWorktreeRef: prep.branch,
          associatedAgentId: prep.agent.id,
          parentThreadId: null,
          subagentAgentId: null,
          subagentNickname: null,
          subagentRole: null,
          createdAt,
        });

        if (watcher) {
          const outcome = await watcher;
          if (!outcome.ok) {
            const headline = `Setup hook failed${
              outcome.exitCode !== null ? ` (exit ${outcome.exitCode})` : ""
            }`;
            const tail = outcome.tail.trim();
            const err = new Error(headline) as Error & { detail?: string };
            if (tail.length > 0) {
              err.detail = tail;
            }
            throw err;
          }
        }
      };

      setIsPending(true);
      try {
        await run();
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : String(cause);
        const detail =
          cause instanceof Error && "detail" in cause
            ? (cause as Error & { detail?: string }).detail
            : undefined;
        setError({
          message,
          ...(detail !== undefined ? { detail } : {}),
          retry: async () => {
            setError(null);
            await run();
          },
        });
      } finally {
        setIsPending(false);
      }
    },
    [
      navigate,
      openTerminalThreadPage,
      settings.defaultProvider,
      userDefaultModel,
      modelSelectionPolicy,
    ],
  );

  return { handleNewWorktreeThread, error, dismissError, isPending };
}
